import React, { useEffect } from 'react';
import * as Bixby from '$/core';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import { setCurrentClient } from '$/store/client/actions';
import { Routes, Route, useMatch } from 'react-router-dom';
import HeadBlock from '$/components/global/heading';
import HeadTab from '$/components/global/headingTab';
import {
  getIsCurrentUserSuperAdminState,
  getIsCurrentUserDocHostingAdminState,
  getIsCurrentUserBixbyComplianceAdminState,
} from '$/store/profile/selectors';
import {
  getBixbyComplianceFeatureFlagState,
  getCloPermissioningEnabled,
} from '$/store/release-feature-flags/selectors';
import ClientUserAccess from './ClientUserAccess';
import ClientCompanyAccess from './ClientCompanyAccess';
import ClientInfo from './ClientInfo';
import ClientApiAccess from './ClientApiAccess';
import ClientTasksPostings from './ClientTasksPostings';
import ClientDataSite from './ClientDataSite';
import ClientDocumentHosting from './ClientDocumentHosting';
import ClientCloAccessRoot from './ClientCloAccess';
import ClientCloDealPermissioningRoot from './ClientCloDealPermissioning';
import ClientCompanyDeclarationForm from '$/pages/admin/client/client-detail/ClientCompanyDeclarationForm';

interface IClientProps {
  currentClient?: Bixby.Models.Client;
  isLoading: boolean;
  isSuperAdmin: boolean;
  isDocHostingAdmin: boolean;
  isCloPermissioningEnabled: boolean;
  isBixbyComplianceAdmin: boolean;
  isBixbyComplianceEnabled: boolean;
  dispatchSetCurrentClient: (clientSlim?: Bixby.Models.ClientSlim) => Promise<void>;
}

const Client: React.FC<IClientProps> = props => {
  const {
    currentClient,
    isLoading,
    dispatchSetCurrentClient,
    isSuperAdmin,
    isDocHostingAdmin,
    isCloPermissioningEnabled,
    isBixbyComplianceAdmin,
    isBixbyComplianceEnabled,
  } = props;
  const match = useMatch('/admin/client/:clientId/*');
  useEffect(() => {
    const getClientDispatcher = async () => {
      if ((!isLoading && match) || (currentClient && match && currentClient.id !== match.params.clientId)) {
        const slim: Bixby.Models.ClientSlim = { id: match.params.clientId } as any;
        await dispatchSetCurrentClient(slim);
      }
    };
    getClientDispatcher();
  }, [isLoading, currentClient, match, dispatchSetCurrentClient]);
  const baseUrl = `/admin/client/${match?.params.clientId}`;

  const publishProps = {
    enabled: false,
    publishEnabled: false,
    seeAllLink: '/admin/client',
  };

  return currentClient && match && currentClient.id === match.params.clientId ? (
    <React.Fragment>
      <HeadBlock title={currentClient.name} publishButton={publishProps}>
        <HeadTab title="Account & Profile" link={baseUrl} exact={true} />
        <HeadTab title="Company Access" link={`${baseUrl}/company-access`} exact={true} />
        <HeadTab title="User Access" link={`${baseUrl}/user-access`} exact={true} />
        <HeadTab title="CLO Access" link={`${baseUrl}/clo-access`} exact={true} />
        {isCloPermissioningEnabled && currentClient.hasCloPermissions && isSuperAdmin && (
          <HeadTab title="CLO Permissioning" link={`${baseUrl}/clo-permissioning`} exact={true} />
        )}
        {isSuperAdmin && <HeadTab title="API Access" link={`${baseUrl}/api-access`} />}
        <HeadTab title="Tasks and Postings" link={`${baseUrl}/tasks-postings`} exact={true} />
        <HeadTab title="Data Sites" link={`${baseUrl}/data-sites`} exact={true} />
        {isDocHostingAdmin && <HeadTab title="Document Hosting" link={`${baseUrl}/document-hosting`} exact={true} />}
        {isBixbyComplianceAdmin && isBixbyComplianceEnabled && currentClient && currentClient.hasBixbyCompliance && (
          <HeadTab title="Declaration Form" link={`${baseUrl}/declaration-form`} exact={true} />
        )}
      </HeadBlock>
      <div>
        <Routes>
          <Route path={'/*'} element={<ClientInfo />} />
          <Route path={'/company-access'} element={<ClientCompanyAccess />} />
          <Route path={'/user-access'} element={<ClientUserAccess />} />
          <Route path={'/clo-access'} element={<ClientCloAccessRoot />} />
          {isCloPermissioningEnabled && (
            <Route path={'/clo-permissioning'} element={<ClientCloDealPermissioningRoot />} />
          )}
          {isSuperAdmin && <Route path={'/api-access'} element={<ClientApiAccess />} />}
          <Route path={'/tasks-postings'} element={<ClientTasksPostings />} />
          <Route path={'/data-sites/*'} element={<ClientDataSite />} />
          {isDocHostingAdmin && <Route path={'/document-hosting'} element={<ClientDocumentHosting />} />}
          {isBixbyComplianceAdmin && isBixbyComplianceEnabled && currentClient && currentClient.hasBixbyCompliance && (
            <Route path="/declaration-form" element={<ClientCompanyDeclarationForm />} />
          )}
        </Routes>
      </div>
    </React.Fragment>
  ) : null;
};

const mapStateToProps = (state: AppState) => {
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  const { isDocHostingAdmin } = getIsCurrentUserDocHostingAdminState(state);
  const { isCloPermissioningEnabled } = getCloPermissioningEnabled(state);
  const { isBixbyComplianceAdmin } = getIsCurrentUserBixbyComplianceAdminState(state);
  const { isBixbyComplianceEnabled } = getBixbyComplianceFeatureFlagState(state);

  return {
    isSuperAdmin,
    currentClient: state.clientState.currentClient,
    isDocHostingAdmin,

    // TODO - refactor
    isLoading: state.loginState.isLoading && state.clientState.isLoading,

    isCloPermissioningEnabled,
    isBixbyComplianceAdmin,
    isBixbyComplianceEnabled,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchSetCurrentClient: (clientSlim?: Bixby.Models.ClientSlim) => dispatch(setCurrentClient(clientSlim)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Client);
